import { createSelector } from '@reduxjs/toolkit';
import { RootState } from '../store';
import { ColumnCustomizationState } from './reducer';

/**
 * Selectors for column customization state
 */

export const selectCustomFilterDrawer = (state: RootState) => state.investor.customFilterDrawer;

// Base selector for raw column customization state
const selectRawColumnCustomizationState = (state: RootState) => state.investor.customFilterDrawer.columnCustomization;

// Default fallback - this is constant and won't cause re-renders
const defaultColumnCustomizationState: ColumnCustomizationState = {
    open: false,
    sourceKey: '',
    selectedColumns: [],
    tempSelectedColumns: [],
    isModified: false,
};

// Memoized selectors to prevent unnecessary re-renders
export const selectColumnCustomization = createSelector(
    [selectRawColumnCustomizationState],
    (columnCustomization): ColumnCustomizationState => columnCustomization || defaultColumnCustomizationState,
);

export const selectColumnCustomizationOpen = createSelector(
    [selectRawColumnCustomizationState],
    (columnCustomization): boolean => columnCustomization?.open || false,
);

export const selectColumnCustomizationSourceKey = createSelector(
    [selectRawColumnCustomizationState],
    (columnCustomization): string => columnCustomization?.sourceKey || '',
);

export const selectSelectedColumns = createSelector(
    [selectRawColumnCustomizationState],
    (columnCustomization): string[] => columnCustomization?.selectedColumns || [],
);

export const selectTempSelectedColumns = createSelector(
    [selectRawColumnCustomizationState],
    (columnCustomization): string[] => columnCustomization?.tempSelectedColumns || [],
);

export const selectColumnCustomizationIsModified = createSelector(
    [selectRawColumnCustomizationState],
    (columnCustomization): boolean => columnCustomization?.isModified || false,
);

/**
 * Selector to check if column customization has unsaved changes
 */
export const selectHasUnsavedColumnChanges = createSelector(
    [selectSelectedColumns, selectTempSelectedColumns],
    (selectedColumns, tempSelectedColumns): boolean => JSON.stringify(selectedColumns) !== JSON.stringify(tempSelectedColumns),
);

/**
 * Selector to check if any columns are currently selected in temp state
 */
export const selectHasTempSelectedColumns = createSelector([selectTempSelectedColumns], (tempColumns): boolean => tempColumns.length > 0);
